// Shared currency utilities. The selected currency is persisted to
// localStorage under a single key and broadcast to all components via a
// custom "currencychange" window event so both guest-mode and signed-in
// dashboards stay in sync without needing a full app context rewire.

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'cashforth_currency';
const DEFAULT_CURRENCY = 'USD';
const EVENT_NAME = 'cashforth:currencychange';

// Major world currencies. `label` is what we show in the dropdown trigger
// (kept short/minimalist: symbol + ISO code).
export const CURRENCIES = [
  { code: 'USD', symbol: '$',  name: 'US Dollar',         locale: 'en-US' },
  { code: 'EUR', symbol: '€',  name: 'Euro',              locale: 'de-DE' },
  { code: 'GBP', symbol: '£',  name: 'British Pound',     locale: 'en-GB' },
  { code: 'JPY', symbol: '¥',  name: 'Japanese Yen',      locale: 'ja-JP' },
  { code: 'CNY', symbol: '¥',  name: 'Chinese Yuan',      locale: 'zh-CN' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar',   locale: 'en-CA' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', locale: 'en-AU' },
  { code: 'CHF', symbol: 'CHF',name: 'Swiss Franc',       locale: 'de-CH' },
  { code: 'INR', symbol: '₹',  name: 'Indian Rupee',      locale: 'en-IN' },
  { code: 'MXN', symbol: 'Mex$',name:'Mexican Peso',      locale: 'es-MX' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real',    locale: 'pt-BR' },
  { code: 'KRW', symbol: '₩',  name: 'South Korean Won',  locale: 'ko-KR' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar',  locale: 'en-SG' },
  { code: 'HKD', symbol: 'HK$',name: 'Hong Kong Dollar',  locale: 'en-HK' },
  { code: 'NZD', symbol: 'NZ$',name: 'New Zealand Dollar',locale: 'en-NZ' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona',     locale: 'sv-SE' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone',   locale: 'nb-NO' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone',      locale: 'da-DK' },
  { code: 'ZAR', symbol: 'R',  name: 'South African Rand',locale: 'en-ZA' },
  { code: 'AED', symbol: 'د.إ',name: 'UAE Dirham',        locale: 'ar-AE' },
];

const BY_CODE = CURRENCIES.reduce((acc, c) => { acc[c.code] = c; return acc; }, {});

export function getStoredCurrency() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && BY_CODE[v] ? v : DEFAULT_CURRENCY;
  } catch {
    return DEFAULT_CURRENCY;
  }
}

export function setStoredCurrency(code) {
  if (!BY_CODE[code]) return;
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch { /* ignore quota/privacy-mode errors */ }
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { code } }));
}

export function getCurrencyInfo(code) {
  return BY_CODE[code] || BY_CODE[DEFAULT_CURRENCY];
}

export function getCurrencySymbol(code) {
  return getCurrencyInfo(code).symbol;
}

// Format an amount using the given (or current) currency code. We use
// Intl with `style: 'currency'`; if the runtime can't format the code we
// fall back to a simple "<symbol><amount>" representation.
export function formatCurrency(amount, code) {
  const info = getCurrencyInfo(code || getStoredCurrency());
  const zeroDecimal = info.code === 'JPY' || info.code === 'KRW';
  try {
    return new Intl.NumberFormat(info.locale, {
      style: 'currency',
      currency: info.code,
      minimumFractionDigits: zeroDecimal ? 0 : 2,
      maximumFractionDigits: zeroDecimal ? 0 : 2,
    }).format(amount);
  } catch {
    const n = Number(amount || 0).toFixed(zeroDecimal ? 0 : 2);
    return `${info.symbol}${n}`;
  }
}

// React hook. Returns [code, setCode]. Updates when any component (or
// another tab) changes the stored currency.
export function useCurrency() {
  const [code, setCode] = useState(getStoredCurrency);

  useEffect(() => {
    const onChange = (e) => {
      const next = (e && e.detail && e.detail.code) || getStoredCurrency();
      setCode(next);
    };
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setCode(getStoredCurrency());
    };
    window.addEventListener(EVENT_NAME, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return [code, setStoredCurrency];
}
